/**
 * Componente de histórico de envíos a FACe
 * Incluye consulta de estado (individual y masiva) y anulación de facturas
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Send,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  FileText,
  RefreshCw,
  Ban,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@/contexts/UserContext";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { FaceSeguimiento } from "@shared/types";
import { estadoDesdeCodigoFace } from "@shared/faceEstados";

interface FaceEnvioLog {
  id: string;
  timestamp: string;
  entorno: "pruebas" | "produccion";
  facturaId: string;
  numeroFactura: string;
  clienteId: string;
  clienteNombre: string;
  oficinaContable?: string;
  organoGestor?: string;
  unidadTramitadora?: string;
  numeroRegistro?: string;
  csv?: string;
  // Resultado del ENVÍO (no el estado de tramitación en FACe: ese va en `seguimiento`)
  codigoEstado: string;
  descripcionEstado?: string;
  seguimiento?: FaceSeguimiento;
  success: boolean;
  /** @deprecated Heredado: ya no se escribe; la anulación real se lee de `seguimiento` (4300). */
  anulada?: boolean;
  // Solicitud de anulación enviada a FACe (no es una anulación hasta que FACe la acepta)
  anulacionSolicitada?: boolean;
  // Seguimiento automático pausado tras varios fallos seguidos de consulta a FACe
  fallosConsecutivos?: number;
  seguimientoPausado?: boolean;
  motivoAnulacion?: string;
  fechaSolicitudAnulacion?: string;
  errores?: Array<{
    codigo: string;
    descripcion: string;
    campo?: string;
  }>;
  tiempoRespuesta?: number;
}

const ETIQUETA_ESTADO: Record<string, string> = {
  REGISTRADA: "Registrada",
  CONTABILIZADA: "Contabilizada",
  PAGADA: "Pagada",
  RECHAZADA: "Rechazada",
  ANULADA: "Anulada en FACe",
  ACEPTADA: "Aceptada",
  ENVIADA: "Enviada",
  PENDIENTE: "Pendiente",
};

/** Anulación solicitada (4200) o rechazada (4400): conviene mostrarla junto al estado. */
const CODIGOS_ANULACION_VISIBLES = ["4200", "4400"];

/**
 * El seguimiento de FACe responde a la solicitud de anulación cuando lleva un
 * código 4200/4300/4400 consultado después de pedirla (si no, es un dato anterior).
 */
const CODIGOS_RESPUESTA_ANULACION = ["4200", "4300", "4400"];
const faceRespondioAnulacion = (envio: FaceEnvioLog) => {
  const seg = envio.seguimiento;
  if (!seg?.codigoAnulacion || !CODIGOS_RESPUESTA_ANULACION.includes(seg.codigoAnulacion)) return false;
  // Instantes, no texto: «…00Z» y «…00.500Z» se ordenan mal como cadenas.
  return !envio.fechaSolicitudAnulacion ||
    Date.parse(seg.fechaConsulta) >= Date.parse(envio.fechaSolicitudAnulacion);
};

/** Anulación confirmada por FACe (4300). */
const estaAnuladaEnFace = (envio: FaceEnvioLog) =>
  envio.seguimiento?.estado === "ANULADA" || envio.seguimiento?.codigoAnulacion === "4300";

/** Solicitud enviada sin respuesta de FACe todavía, o con FACe tramitándola (4200). */
const anulacionPendiente = (envio: FaceEnvioLog) => {
  if (!envio.anulacionSolicitada && envio.seguimiento?.codigoAnulacion !== "4200") return false;
  const rechazada = envio.seguimiento?.codigoAnulacion === "4400" && faceRespondioAnulacion(envio);
  return !rechazada;
};

/** Se puede solicitar la anulación salvo que ya esté anulada o haya una solicitud pendiente. */
const puedeSolicitarAnulacion = (envio: FaceEnvioLog) =>
  !!envio.numeroRegistro && !estaAnuladaEnFace(envio) && !anulacionPendiente(envio);

const formatearFecha = (iso: string) =>
  new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });

/** Datos de estado que devuelven las consultas de FACe (individual y lote). */
interface RespuestaEstado {
  codigoEstado: string;
  descripcionEstado?: string;
  motivoEstado?: string;
  codigoAnulacion?: string;
  descripcionAnulacion?: string;
  guardado?: boolean;
}

const seguimientoDesdeRespuesta = (data: RespuestaEstado): FaceSeguimiento => ({
  codigo: data.codigoEstado,
  descripcion: data.descripcionEstado || undefined,
  motivo: data.motivoEstado || undefined,
  codigoAnulacion: data.codigoAnulacion || undefined,
  descripcionAnulacion: data.descripcionAnulacion || undefined,
  estado: estadoDesdeCodigoFace(data.codigoEstado, data.codigoAnulacion),
  fechaConsulta: new Date().toISOString(),
});

const AVISO_NO_GUARDADO = "El estado se obtuvo de FACe pero no se ha guardado.";

export function FaceEnvios() {
  const { toast } = useToast();
  const { fetchWithAuth } = useUser();
  const [envios, setEnvios] = useState<FaceEnvioLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEnvio, setSelectedEnvio] = useState<FaceEnvioLog | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Estado para actualización masiva
  const [updatingAll, setUpdatingAll] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Estado para anulación
  const [anularDialogOpen, setAnularDialogOpen] = useState(false);
  const [envioToAnular, setEnvioToAnular] = useState<FaceEnvioLog | null>(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  const [anulando, setAnulando] = useState(false);

  useEffect(() => {
    loadEnvios();
  }, []);

  const MENSAJE_ERROR_CARGA = "No se pudo cargar el histórico de envíos";

  const loadEnvios = async () => {
    setLoading(true);
    setErrorCarga(null);
    try {
      const response = await fetchWithAuth("/api/face/envios?limit=100");
      if (!response.ok) {
        // Un fallo de la API no es «sin envíos»: se avisa en vez de dejar la lista vacía.
        throw new Error(`Respuesta ${response.status}`);
      }
      const data = await response.json();
      setEnvios(data);
    } catch (error) {
      console.error("Error al cargar envíos:", error);
      setErrorCarga(MENSAJE_ERROR_CARGA);
      toast({
        title: "Error",
        description: MENSAJE_ERROR_CARGA,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Consultar estado individual ──────────────────────────────────────────
  const handleActualizarEstado = async (envio: FaceEnvioLog) => {
    if (!envio.numeroRegistro) return;
    setUpdatingId(envio.id);

    try {
      const response = await fetchWithAuth(
        `/api/face/estado/${encodeURIComponent(envio.numeroRegistro)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success === true) {
          const nuevo = seguimientoDesdeRespuesta(data);
          // Una consulta correcta reanuda el seguimiento automático (el servidor también).
          const reanudado = { seguimiento: nuevo, seguimientoPausado: false, fallosConsecutivos: 0 };
          setEnvios((prev) => prev.map((e) => (e.id === envio.id ? { ...e, ...reanudado } : e)));
          setSelectedEnvio((sel) => (sel && sel.id === envio.id ? { ...sel, ...reanudado } : sel));
          toast({
            title: "Estado actualizado",
            description: `${envio.numeroFactura}: ${data.descripcionEstado || data.codigoEstado}${
              data.guardado === false ? `. ${AVISO_NO_GUARDADO}` : ""
            }`,
          });
        } else {
          // Una consulta fallida no sustituye al último estado conocido.
          toast({
            title: "No se pudo consultar el estado",
            description: `${envio.numeroFactura}: ${data.descripcionEstado || data.codigoEstado || "sin respuesta de FACe"}`,
            variant: "destructive",
          });
        }
      } else {
        const err = await response.json();
        toast({
          title: "Error",
          description: err.message || "No se pudo consultar el estado",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error al consultar estado:", error);
      toast({
        title: "Error",
        description: "Error de conexión al consultar estado",
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  // ─── Consultar estado masivo ──────────────────────────────────────────────
  const handleActualizarTodos = async () => {
    const conRegistro = envios.filter((e) => e.numeroRegistro);
    if (conRegistro.length === 0) {
      toast({ title: "Sin facturas", description: "No hay facturas con número de registro para consultar." });
      return;
    }

    setUpdatingAll(true);
    try {
      const numerosRegistro = conRegistro.map((e) => e.numeroRegistro!);
      const response = await fetchWithAuth("/api/face/estado/lote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ numerosRegistro }),
      });

      if (response.ok) {
        const estados: Array<RespuestaEstado & { numeroRegistro: string; ok: boolean }> =
          await response.json();

        const correctos = estados.filter((e) => e.ok === true);
        const sinGuardar = correctos.filter((e) => e.guardado === false).length;
        const estadoMap = new Map(correctos.map((e) => [e.numeroRegistro, seguimientoDesdeRespuesta(e)]));

        setEnvios((prev) =>
          prev.map((envio) => {
            const nuevo = envio.numeroRegistro ? estadoMap.get(envio.numeroRegistro) : undefined;
            return nuevo ? { ...envio, seguimiento: nuevo, seguimientoPausado: false, fallosConsecutivos: 0 } : envio;
          })
        );
        setSelectedEnvio((sel) => {
          const nuevo = sel?.numeroRegistro ? estadoMap.get(sel.numeroRegistro) : undefined;
          return sel && nuevo ? { ...sel, seguimiento: nuevo, seguimientoPausado: false, fallosConsecutivos: 0 } : sel;
        });

        toast({
          title: "Estados actualizados",
          description:
            `Se actualizaron ${correctos.length} de ${numerosRegistro.length} facturas` +
            (sinGuardar > 0 ? `. ${sinGuardar} no se ha${sinGuardar > 1 ? "n" : ""} guardado.` : ""),
        });
      } else {
        const err = await response.json();
        toast({
          title: "Error",
          description: err.message || "Error al consultar estados en lote",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error al actualizar estados:", error);
      toast({
        title: "Error",
        description: "Error de conexión al actualizar estados",
        variant: "destructive",
      });
    } finally {
      setUpdatingAll(false);
    }
  };

  // ─── Anulación ────────────────────────────────────────────────────────────
  const handleOpenAnular = (envio: FaceEnvioLog) => {
    setEnvioToAnular(envio);
    setMotivoAnulacion("");
    setAnularDialogOpen(true);
  };

  const handleConfirmarAnulacion = async () => {
    if (!envioToAnular?.numeroRegistro || motivoAnulacion.trim().length < 5) return;

    setAnulando(true);
    try {
      const response = await fetchWithAuth(
        `/api/face/anular/${encodeURIComponent(envioToAnular.numeroRegistro)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ motivo: motivoAnulacion }),
        }
      );

      if (response.ok) {
        const solicitud = {
          anulacionSolicitada: true,
          motivoAnulacion: motivoAnulacion.trim(),
          fechaSolicitudAnulacion: new Date().toISOString(),
        };
        setEnvios((prev) => prev.map((e) => (e.id === envioToAnular.id ? { ...e, ...solicitud } : e)));
        setSelectedEnvio((sel) => (sel?.id === envioToAnular.id ? { ...sel, ...solicitud } : sel));
        toast({
          title: "Anulación solicitada",
          description:
            `FACe ha registrado la solicitud de anulación de la factura ${envioToAnular.numeroFactura}. ` +
            "El estado se actualizará cuando la administración la acepte o la rechace.",
        });
        setAnularDialogOpen(false);
      } else {
        const err = await response.json();
        toast({
          title: "No se pudo solicitar la anulación",
          description: err.message || "No se pudo solicitar la anulación de la factura",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error al anular factura:", error);
      toast({
        title: "Error",
        description: "Error de conexión al solicitar la anulación de la factura",
        variant: "destructive",
      });
    } finally {
      setAnulando(false);
    }
  };

  // ─── Helpers de UI ────────────────────────────────────────────────────────
  const handleVerDetalles = (envio: FaceEnvioLog) => {
    setSelectedEnvio(envio);
    setDialogOpen(true);
  };

  const filteredEnvios = envios.filter((envio) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      envio.numeroFactura.toLowerCase().includes(searchLower) ||
      envio.clienteNombre.toLowerCase().includes(searchLower) ||
      envio.numeroRegistro?.toLowerCase().includes(searchLower) ||
      envio.csv?.toLowerCase().includes(searchLower)
    );
  });

  const getEstadoBadge = (envio: FaceEnvioLog) => {
    if (!envio.success) {
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    }

    // Solicitud enviada que FACe aún no ha reflejado en su estado: no es una anulación.
    const solicitudSinRespuesta = !!envio.anulacionSolicitada && !faceRespondioAnulacion(envio);
    const etiquetaSolicitud = solicitudSinRespuesta && (
      <Badge variant="secondary" className="text-xs">
        Anulación solicitada
      </Badge>
    );
    const etiquetaPausa = envio.seguimientoPausado === true && (
      <Badge
        variant="outline"
        className="text-xs text-amber-700 border-amber-300"
        title="FACe no ha devuelto esta factura en 4 consultas automáticas seguidas. Pulsa «Actualizar estado» para reanudar el seguimiento."
      >
        Seguimiento en pausa
      </Badge>
    );

    const seg = envio.seguimiento;
    if (!seg) {
      return (
        <div className="flex flex-col items-start gap-1">
          <Badge variant="default" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Enviada
          </Badge>
          <span className="text-xs text-muted-foreground">sin consultar</span>
          {etiquetaSolicitud}
          {etiquetaPausa}
        </div>
      );
    }

    const texto =
      seg.descripcion || (seg.estado && ETIQUETA_ESTADO[seg.estado]) || `Código ${seg.codigo}`;
    const variante = seg.estado === "RECHAZADA" ? "destructive" : seg.estado ? "default" : "outline";
    const Icono = seg.estado === "RECHAZADA" ? XCircle : seg.estado === "ANULADA" ? Ban : CheckCircle2;
    const mostrarAnulacion =
      seg.codigoAnulacion && CODIGOS_ANULACION_VISIBLES.includes(seg.codigoAnulacion);

    return (
      <div
        className="flex flex-col items-start gap-1"
        title={`Estado en FACe consultado el ${formatearFecha(seg.fechaConsulta)}`}
      >
        <Badge
          variant={variante}
          className={cn(
            "gap-1",
            seg.estado === "PAGADA" && "bg-green-600 text-white hover:bg-green-600/90",
            seg.estado === "ANULADA" && "bg-transparent text-orange-600 border-orange-300 hover:bg-transparent"
          )}
        >
          <Icono className="h-3 w-3" />
          {texto}
        </Badge>
        {mostrarAnulacion && (
          <Badge variant="secondary" className="text-xs">
            {seg.descripcionAnulacion || `Anulación ${seg.codigoAnulacion}`}
          </Badge>
        )}
        {etiquetaSolicitud}
        {etiquetaPausa}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Histórico de Envíos a FACe
              </CardTitle>
              <CardDescription>
                Consulta el estado de las facturas enviadas a Administraciones Públicas
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleActualizarTodos}
              disabled={updatingAll || envios.length === 0}
              className="gap-2"
            >
              {updatingAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Actualizar estados
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Buscador */}
          <div className="mb-4">
            <Input
              placeholder="Buscar por número de factura, cliente, registro o CSV..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Tabla de envíos */}
          {errorCarga ? (
            <div role="alert" className="text-center py-12">
              <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
              <p className="text-destructive mb-4">{errorCarga}</p>
              <Button variant="outline" size="sm" onClick={loadEnvios} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Reintentar
              </Button>
            </div>
          ) : filteredEnvios.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? "No se encontraron envíos" : "Aún no has enviado facturas a FACe"}
              </p>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Factura</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Nº Registro</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Entorno</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEnvios.map((envio) => (
                    <TableRow key={envio.id} className={estaAnuladaEnFace(envio) ? "opacity-60" : ""}>
                      <TableCell className="text-sm">
                        {formatDistanceToNow(new Date(envio.timestamp), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </TableCell>
                      <TableCell className="font-medium">{envio.numeroFactura}</TableCell>
                      <TableCell className="text-sm">{envio.clienteNombre}</TableCell>
                      <TableCell className="text-sm font-mono">
                        {envio.numeroRegistro || "-"}
                      </TableCell>
                      <TableCell>{getEstadoBadge(envio)}</TableCell>
                      <TableCell>
                        <Badge variant={envio.entorno === "produccion" ? "default" : "outline"}>
                          {envio.entorno === "produccion" ? "Producción" : "Pruebas"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleVerDetalles(envio)}
                            title="Ver detalles"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {envio.numeroRegistro && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleActualizarEstado(envio)}
                              disabled={updatingId === envio.id}
                              title="Actualizar estado"
                            >
                              {updatingId === envio.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {puedeSolicitarAnulacion(envio) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenAnular(envio)}
                              title="Anular factura"
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-950"
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {filteredEnvios.length > 50 && (
            <div className="flex items-center justify-center mt-4">
              <p className="text-sm text-muted-foreground">
                Mostrando {Math.min(50, filteredEnvios.length)} de {filteredEnvios.length} envíos
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de detalles */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalles del Envío</DialogTitle>
            <DialogDescription>
              Información completa del envío a FACe
            </DialogDescription>
          </DialogHeader>

          {selectedEnvio && (
            <div className="space-y-6">
              {/* Información general */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Factura</p>
                  <p className="text-lg font-semibold">{selectedEnvio.numeroFactura}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Cliente</p>
                  <p className="text-lg font-semibold">{selectedEnvio.clienteNombre}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Fecha de envío</p>
                  <p>
                    {new Date(selectedEnvio.timestamp).toLocaleString("es-ES", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Entorno</p>
                  <Badge variant={selectedEnvio.entorno === "produccion" ? "default" : "outline"}>
                    {selectedEnvio.entorno === "produccion" ? "Producción" : "Pruebas"}
                  </Badge>
                </div>
              </div>

              {/* Estado en FACe (última consulta) */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Estado en FACe</h4>
                {selectedEnvio.seguimiento ? (
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-muted-foreground">Descripción</p>
                      <p>
                        {selectedEnvio.seguimiento.descripcion ||
                          (selectedEnvio.seguimiento.estado &&
                            ETIQUETA_ESTADO[selectedEnvio.seguimiento.estado]) ||
                          "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Código de tramitación</p>
                      <p className="font-mono">{selectedEnvio.seguimiento.codigo}</p>
                    </div>
                    {selectedEnvio.seguimiento.motivo && (
                      <div>
                        <p className="text-sm text-muted-foreground">Motivo</p>
                        <p>{selectedEnvio.seguimiento.motivo}</p>
                      </div>
                    )}
                    {selectedEnvio.seguimiento.codigoAnulacion && (
                      <div>
                        <p className="text-sm text-muted-foreground">Anulación en FACe</p>
                        <p>
                          <span className="font-mono">{selectedEnvio.seguimiento.codigoAnulacion}</span>
                          {selectedEnvio.seguimiento.descripcionAnulacion &&
                            ` · ${selectedEnvio.seguimiento.descripcionAnulacion}`}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-muted-foreground">Última consulta</p>
                      <p>{formatearFecha(selectedEnvio.seguimiento.fechaConsulta)}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Aún no se ha consultado el estado en FACe.
                  </p>
                )}
              </div>

              {/* Resultado del envío */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Resultado del envío</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {selectedEnvio.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="font-medium">
                      {selectedEnvio.success ? "Enviada correctamente" : "Error en el envío"}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Código del envío</p>
                    <p className="font-mono">{selectedEnvio.codigoEstado}</p>
                  </div>
                  {selectedEnvio.descripcionEstado && (
                    <div>
                      <p className="text-sm text-muted-foreground">Descripción</p>
                      <p>{selectedEnvio.descripcionEstado}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Solicitud de anulación (FACe decide en `seguimiento`) */}
              {selectedEnvio.anulacionSolicitada && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-orange-600">
                    <Ban className="h-5 w-5" />
                    Solicitud de anulación
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-muted-foreground">Motivo</p>
                      <p>{selectedEnvio.motivoAnulacion}</p>
                    </div>
                    {selectedEnvio.fechaSolicitudAnulacion && (
                      <div>
                        <p className="text-sm text-muted-foreground">Fecha de la solicitud</p>
                        <p>{formatearFecha(selectedEnvio.fechaSolicitudAnulacion)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Registro FACe */}
              {selectedEnvio.numeroRegistro && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Registro en FACe</h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-muted-foreground">Número de registro</p>
                      <p className="font-mono text-lg">{selectedEnvio.numeroRegistro}</p>
                    </div>
                    {selectedEnvio.csv && (
                      <div>
                        <p className="text-sm text-muted-foreground">
                          CSV (Código Seguro de Verificación)
                        </p>
                        <p className="font-mono">{selectedEnvio.csv}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Códigos DIR3 */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Códigos DIR3</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Oficina Contable</p>
                    <p className="font-mono">{selectedEnvio.oficinaContable || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Órgano Gestor</p>
                    <p className="font-mono">{selectedEnvio.organoGestor || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Unidad Tramitadora</p>
                    <p className="font-mono">{selectedEnvio.unidadTramitadora || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Errores */}
              {selectedEnvio.errores && selectedEnvio.errores.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-5 w-5" />
                    Errores Detectados
                  </h4>
                  <div className="space-y-2">
                    {selectedEnvio.errores.map((error, index) => (
                      <div key={index} className="bg-red-50 dark:bg-red-950 p-3 rounded border border-red-200 dark:border-red-800">
                        <div className="flex items-start gap-2">
                          <Badge variant="destructive" className="mt-0.5">
                            {error.codigo}
                          </Badge>
                          <div className="flex-1">
                            <p className="text-sm">{error.descripcion}</p>
                            {error.campo && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Campo: {error.campo}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Información técnica */}
              {selectedEnvio.tiempoRespuesta && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 text-sm">Información Técnica</h4>
                  <div className="text-sm text-muted-foreground">
                    <p>Tiempo de respuesta: {selectedEnvio.tiempoRespuesta}ms</p>
                    <p>ID de envío: {selectedEnvio.id}</p>
                  </div>
                </div>
              )}

              {/* Acciones desde el dialog */}
              {selectedEnvio.numeroRegistro && (
                <div className="border-t pt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      handleActualizarEstado(selectedEnvio);
                    }}
                    disabled={updatingId === selectedEnvio.id}
                  >
                    {updatingId === selectedEnvio.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Actualizar estado
                  </Button>
                  {puedeSolicitarAnulacion(selectedEnvio) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 text-orange-600 border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950"
                      onClick={() => {
                        setDialogOpen(false);
                        handleOpenAnular(selectedEnvio);
                      }}
                    >
                      <Ban className="h-4 w-4" />
                      Anular factura
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de anulación */}
      <AlertDialog open={anularDialogOpen} onOpenChange={setAnularDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Solicitar la anulación en FACe</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a solicitar la anulación de la factura{" "}
              <strong>{envioToAnular?.numeroFactura}</strong> (registro{" "}
              <span className="font-mono">{envioToAnular?.numeroRegistro}</span>).
              La factura no queda anulada hasta que la administración acepte la solicitud;
              también puede rechazarla.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="motivo-anulacion">Motivo de la anulación</Label>
            <Textarea
              id="motivo-anulacion"
              placeholder="Indica el motivo de la anulación (mínimo 5 caracteres)..."
              value={motivoAnulacion}
              onChange={(e) => setMotivoAnulacion(e.target.value)}
              rows={3}
            />
            {motivoAnulacion.length > 0 && motivoAnulacion.trim().length < 5 && (
              <p className="text-sm text-red-500">El motivo debe tener al menos 5 caracteres</p>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={anulando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarAnulacion}
              disabled={anulando || motivoAnulacion.trim().length < 5}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {anulando ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Solicitando...
                </>
              ) : (
                "Solicitar anulación"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
